import React from 'react';
import { useLocale } from 'locale';
import * as Styled from './styled';
import { UserRatings } from 'utils/entities';
import CommunityIcon from 'assets/svg/review-community.svg';
import CleanIcon from 'assets/svg/review-clean.svg';
import SchoolIcon from 'assets/svg/review-school.svg';
import CommuteIcon from 'assets/svg/review-commute.svg';
import ShoppingIcon from 'assets/svg/review-shopping.svg';
import FreeTimeIcon from 'assets/svg/review-free-time.svg';
import { Route } from 'config/routes';
import { useScreenBreakpoint } from 'consts/breakpoints';

interface ReviewProps {
  ratings?: { [rating in UserRatings]?: number | boolean };
  localPageDocId: string;
}

const DISPLAYED_RATINGS: Array<{ icon: JSX.Element; type: UserRatings }> = [
  { icon: <CommunityIcon />, type: 'communityRank' },
  { icon: <CleanIcon />, type: 'cleanRank' },
  { icon: <SchoolIcon />, type: 'schoolsRank' },
  { icon: <CommuteIcon />, type: 'publicTransportRank' },
  { icon: <ShoppingIcon />, type: 'shoppingRank' },
  { icon: <FreeTimeIcon />, type: 'pastimeRank' },
];

function getScoreValue(rating: number) {
  return `${Math.round(rating * 2).toString()}/10`;
}

function getScoreBarValue(rating: number) {
  return rating * 20;
}

export const Review: React.FC<ReviewProps> = (props) => {
  const {
    ratings,
    localPageDocId,
  } = props;

  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const displayRatings = ratings && DISPLAYED_RATINGS.filter(({ type }) => ratings[type]);


  const getTitle = (type: UserRatings) => t('localPage.neighbours.ratings.rated', { ratingKey: type });

  if (!(displayRatings && displayRatings.length)) return null;

  return (
    <Styled.Root data-auto="reviews">
      <Styled.Heading weight="bold">{t('unitPage.ratings.title')}</Styled.Heading>
      <Styled.Main>
        <Styled.ReviewsContainer>
          {displayRatings.map(({ type, icon }) => (
            <Styled.ReviewRow key={type}>
              <Styled.IconWrap>{icon}</Styled.IconWrap>
              {isMobile ? <Styled.ReviewTitle>{getTitle(type)}</Styled.ReviewTitle> : <Styled.MobileReviewTitle>{getTitle(type)}</Styled.MobileReviewTitle>}
              <Styled.ScoreBar rating={getScoreBarValue(ratings[type] as number)} />
              <Styled.ScoreValue weight="bold">{getScoreValue(ratings[type] as number)}</Styled.ScoreValue>
            </Styled.ReviewRow>
          ))}
        </Styled.ReviewsContainer>
        <Styled.StyledRouteLink
          preserveParams
          routeName={Route.LocalPage}
          routeParams={{ id: localPageDocId }}
        >
          <Styled.MoreInfoLink data-auto="opinion-link" small={!isMobile} simple withArrow>{t('unitPage.ratings.more.copy')}</Styled.MoreInfoLink>
        </Styled.StyledRouteLink>
      </Styled.Main>
    </Styled.Root>
  );
};
